import { useWorkspaces } from '../../hooks/use-workspaces';
import type { Workspace } from '../api/types';
import { useWorkspaceContext } from './context';
import { createDefaultWorkspace as apiCreateDefaultWorkspace } from '../api/workspaces';

/**
 * Main workspace hook - provides all workspace functionality
 * This replaces the need for multiple separate hooks
 */
export function useWorkspace() {
  const { activeWorkspaceId, setActiveWorkspaceId, isLoading } = useWorkspaceContext();
  const { data: workspaces, mutate } = useWorkspaces();

  const activeWorkspace = workspaces?.find(w => w.id === activeWorkspaceId) || null;
  const userRole = activeWorkspace?.user_role || null;
  const isAdmin = userRole === 'admin';
  const canEdit = userRole === 'admin' || userRole === 'editor';

  const switchWorkspace = async (workspaceId: string | null) => {
    try {
      await setActiveWorkspaceId(workspaceId);
    } catch (error) {
      console.error('Failed to switch workspace:', error);
      throw error;
    }
  };

  const createDefaultWorkspace = async (data?: { name?: string; description?: string }) => {
    try {
      const result = await apiCreateDefaultWorkspace(data);
      
      // Automatically refetch workspaces after creation
      await mutate();
      
      return result;
    } catch (error) {
      console.error('Failed to create default workspace:', error);
      throw error;
    }
  };

  return {
    // Current workspace state
    activeWorkspace,
    activeWorkspaceId,
    
    // Available workspaces
    workspaces,
    
    // Loading states
    isLoading,
    
    // Actions
    switchWorkspace,
    createDefaultWorkspace,
    
    // Role helpers
    isAdmin,
    canEdit,
    userRole,
  };
}

// Legacy aliases for backwards compatibility
export const useActiveWorkspace = useWorkspace;
export const useAvailableWorkspaces = () => ({ data: useWorkspace().workspaces });
export const useIsActiveWorkspaceAdmin = () => useWorkspace().isAdmin;
export const useCanEditActiveWorkspace = () => useWorkspace().canEdit;
export const useActiveWorkspaceRole = () => useWorkspace().userRole; 